import { useEffect, useRef } from 'react';

export type HotkeyMap = Partial<Record<string, () => void>>;

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

const isControl = (target: EventTarget | null) => target instanceof HTMLElement && ['BUTTON', 'A'].includes(target.tagName);

/**
 * Keys for a screen, by `KeyboardEvent.key`. Letters and digits are ignored while the person types in a
 * field; Escape always reaches its handler. Enter on a focused field, button or link is left to it.
 */
export function useHotkeys(map: HotkeyMap, enabled = true) {
  const latest = useRef(map);
  useEffect(() => {
    latest.current = map;
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) return;
      if (event.key !== 'Escape' && isTyping(event.target)) return;
      if (event.key === 'Enter' && isControl(event.target)) return;
      const handler = latest.current[event.key];
      if (!handler) return;
      event.preventDefault();
      handler();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}
